/**
 * FcmConfigCard — super-admin panel for pasting Firebase credentials so
 * push notifications flip on without a redeploy.
 *
 * Uses the endpoints under /api/admin/fcm-config. The service account
 * private key is stored server-side and never re-served to the client
 * (GET returns a `has_service_account:true` flag + a redacted preview).
 */
import * as React from "react";
import { Bell, Loader2, ShieldCheck, ShieldAlert, RotateCcw, Zap, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface RedactedSA {
  project_id?: string;
  client_email?: string;
  private_key_id?: string;
  has_private_key?: boolean;
}
interface WebCfg {
  project_id?: string;
  api_key?: string;
  auth_domain?: string;
  messaging_sender_id?: string;
  app_id?: string;
  vapid_public_key?: string;
}
interface Cfg {
  service_account?: RedactedSA | null;
  web?: WebCfg;
  updated_at?: string;
  has_service_account?: boolean;
  provider_ready?: boolean;
}

export const FcmConfigCard: React.FC = () => {
  const [cfg, setCfg] = React.useState<Cfg | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [sa, setSa] = React.useState("");
  const [web, setWeb] = React.useState<WebCfg>({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<Cfg>("/admin/fcm-config");
      setCfg(r.data);
      setWeb(r.data?.web || {});
    } catch {
      toast.error("Couldn't load FCM config.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const body: any = { web };
      if (sa.trim()) body.service_account = sa.trim();
      const r = await api.put<Cfg>("/admin/fcm-config", body);
      setCfg(r.data);
      setSa("");  // never keep the raw key in the DOM after save
      toast.success(r.data?.provider_ready ? "FCM live" : "Config saved");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    if (!confirm("Wipe the Firebase credentials from the DB? The env fallback (if any) will reassert.")) return;
    setSaving(true);
    try {
      await api.post("/admin/fcm-config/reset");
      await load();
      toast.success("Cleared. Env fallback (if any) is active.");
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    setTesting(true);
    try {
      const r = await api.post<any>("/admin/fcm-config/test", {});
      if (r.data?.delivered) toast.success(`Test push landed on ${r.data.delivered_tokens_count} device(s)`);
      else toast.warning(`Not delivered — ${r.data?.delivery_error || "no devices registered"}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  }

  const ready = cfg?.provider_ready;

  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900 border border-neutral-200 dark:border-white/10 p-4 sm:p-5 space-y-4" data-testid="admin-fcm-config">
      <header className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl grid place-items-center text-white bg-gradient-to-br ${ready ? "from-emerald-500 to-cyan-500" : "from-orange-500 to-fuchsia-500"}`}>
          <Bell className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-neutral-900 dark:text-white">Firebase Cloud Messaging</div>
          <div className="text-[11px] text-neutral-500 dark:text-slate-400">Paste your service account JSON + web config to turn on mobile + web push.</div>
        </div>
        <div className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-1 border ${
          ready ? "border-emerald-300 text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-400/40"
                : "border-amber-300 text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-500/10 dark:border-amber-400/40"
        }`} data-testid="fcm-status-badge">
          {ready ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
          {ready ? "Live" : "Not configured"}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-neutral-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          {/* Redacted preview of what's currently stored. */}
          {cfg?.service_account?.has_private_key ? (
            <div className="rounded-lg bg-neutral-50 dark:bg-white/5 p-3 space-y-1" data-testid="fcm-current-summary">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 dark:text-slate-400">Currently stored</div>
              <div className="text-[12px] font-mono text-neutral-700 dark:text-slate-200">
                {cfg.service_account.client_email}
              </div>
              <div className="text-[11px] text-neutral-500 dark:text-slate-400">
                project {cfg.service_account.project_id} · private key ends …{cfg.service_account.private_key_id?.slice(-6)}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-neutral-300 dark:border-white/10 p-3 text-[12px] text-neutral-500 dark:text-slate-400" data-testid="fcm-empty-notice">
              No service account stored yet. Push events are queuing.
            </div>
          )}

          {/* Service account paste box. */}
          <div>
            <label className="text-[11px] font-semibold text-neutral-600 dark:text-slate-300">Service Account JSON (Project Settings → Service Accounts → Generate new private key)</label>
            <textarea
              data-testid="fcm-sa-input"
              value={sa}
              onChange={(e) => setSa(e.target.value)}
              placeholder='{"type":"service_account","project_id":"…","private_key":"-----BEGIN PRIVATE KEY-----…","client_email":"…"}'
              spellCheck={false}
              className="mt-1 w-full min-h-[110px] px-3 py-2 rounded-lg text-[11px] font-mono bg-neutral-50 dark:bg-white/5 border border-neutral-200 dark:border-white/10 focus:outline-none focus:border-orange-400"
            />
            <div className="text-[10px] text-neutral-400 mt-1">Never displayed after save — kept server-side, only a redacted preview shows above.</div>
          </div>

          {/* Six web config fields — public, safe to display. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { id: "project_id" as const,           label: "Project ID"           },
              { id: "api_key" as const,              label: "Web API key"          },
              { id: "auth_domain" as const,          label: "Auth domain"          },
              { id: "messaging_sender_id" as const,  label: "Messaging sender ID"  },
              { id: "app_id" as const,               label: "App ID"               },
              { id: "vapid_public_key" as const,     label: "VAPID public key"     },
            ].map(({ id, label }) => (
              <label key={id} className="block text-[11px] font-semibold text-neutral-600 dark:text-slate-300">
                {label}
                <input
                  data-testid={`fcm-web-${id}`}
                  value={web[id] || ""}
                  onChange={(e) => setWeb((prev) => ({ ...prev, [id]: e.target.value }))}
                  className="mt-1 w-full px-2 py-1.5 rounded-lg text-[12px] font-mono bg-neutral-50 dark:bg-white/5 border border-neutral-200 dark:border-white/10 focus:outline-none focus:border-orange-400"
                />
              </label>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              data-testid="fcm-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ChevronRight className="h-4 w-4 mr-1.5" />}
              Save & activate
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={sendTest}
              disabled={testing || !ready}
              data-testid="fcm-test-btn"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />}
              Send test push
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={reset}
              disabled={saving || !cfg?.has_service_account}
              data-testid="fcm-reset-btn"
              className="text-rose-600 hover:text-rose-700"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset
            </Button>
          </div>
        </>
      )}
    </section>
  );
};

export default FcmConfigCard;
